import { useEffectiveFeaturesForCustomer } from '@domains/subscription/priv/Features/api/queries'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import type { GridPaginationModel } from '@mui/x-data-grid-premium'
import { useCallback, useState } from 'react'
import FigmaBox from '../../../mynt-components/components/FigmaBox'
import { useFraudCases } from '../api/useFraudCases'
import type { ListFraudCasesParams } from '../types'
import { FraudCasesFilters } from './FraudCasesFilters'
import { FraudCasesTable } from './FraudCasesTable'

const FRAUD_CUSTOMER_MANAGEMENT_FEATURE_ID = 'a1c8e4b2-7f3d-4e9a-b5c6-8d2f1a0e7b4c'

type CustomerFraudCasesTabProps = {
  customerId: string
}

export const CustomerFraudCasesTab = ({ customerId }: CustomerFraudCasesTabProps) => {
  const [filters, setFilters] = useState<Partial<ListFraudCasesParams>>({ status: ['NEW', 'NOTIFIED'] })
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 })

  const { data: effectiveFeatures = [] } = useEffectiveFeaturesForCustomer({ customerId })
  const fraudFeatureActive = effectiveFeatures.some(
    (f) => f.id === FRAUD_CUSTOMER_MANAGEMENT_FEATURE_ID && f.fields.some((field) => field.type === 'BOOLEAN' && field.value === true),
  )

  const params: ListFraudCasesParams = {
    'customer-id': customerId,
    pageSize: paginationModel.pageSize,
    page: paginationModel.page + 1,
    ...filters,
  }

  const { data, isFetching } = useFraudCases(params)
  const cases = data?.cases ?? []

  const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
    setPaginationModel(model)
  }, [])

  return (
    <FigmaBox fullWidth gap="24px">
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <FraudCasesFilters fields={['case-id', 'person-id', 'card-id']} onSearch={setFilters} />
        <Alert severity={fraudFeatureActive ? 'success' : 'warning'}>
          {fraudFeatureActive
            ? 'Customer should reply to fraud case in their mobile app.'
            : 'Customer should have received an email regarding fraud case if status is NOTIFIED.'}
        </Alert>
      </Box>
      <FraudCasesTable
        cases={cases}
        isLoading={isFetching}
        hiddenColumns={['customerId', 'id']}
        rowCount={data?.pagination?.totalItems ?? 0}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
      />
    </FigmaBox>
  )
}
